import React, { useId, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { ProgressSpinner } from "primereact/progressspinner";
import dayjs from "dayjs";
import EmptyState from "./EmptyState";
import {
  dayKey,
  formatDate,
  groupByDay,
  isProgramPoints,
  withRunningTotals,
} from "./rewardsData";

const PAGE_SIZE = 10;

const FILTERS = [
  { id: "all", label: "my_rewards_filter_all", matches: () => true },
  { id: "programs", label: "my_rewards_filter_programs", matches: isProgramPoints },
  {
    id: "learning-objects",
    label: "learning_objects",
    matches: (entry) => !isProgramPoints(entry),
  },
];

const TrophyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" />
    <path d="M17 5h3a3 3 0 0 1-3 4M7 5H4a3 3 0 0 0 3 4" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12 3 3 5-6" />
  </svg>
);

const PointsEntry = ({ entry }) => {
  const intl = useIntl();
  const isProgram = isProgramPoints(entry);

  return (
    <li className="lr-entry">
      <span
        className={`lr-entry-icon${isProgram ? " lr-entry-icon--program" : ""}`}
        data-testid={isProgram ? "points-icon-program" : "points-icon-learning-object"}
        aria-hidden="true"
      >
        {isProgram ? <TrophyIcon /> : <CheckCircleIcon />}
      </span>
      <div className="lr-entry-text">
        <div className="lr-entry-name">{entry.sourceName || entry.description}</div>
        <div className="lr-entry-type">
          {intl.formatMessage({
            id: isProgram
              ? "my_rewards_program_completed"
              : "my_rewards_learning_object_completed",
          })}
        </div>
      </div>
      <span className="lr-plus">
        {intl.formatMessage(
          { id: "my_rewards_points_gained" },
          { points: intl.formatNumber(entry.points) }
        )}
      </span>
      <span className="lr-run">
        {intl.formatMessage(
          { id: "my_rewards_running_total" },
          { total: intl.formatNumber(entry.runningTotal) }
        )}
      </span>
    </li>
  );
};

const PointsHistory = ({ pointsHistory = [], requesting }) => {
  const intl = useIntl();
  const idPrefix = useId();
  const [filterId, setFilterId] = useState("all");
  const [shown, setShown] = useState(PAGE_SIZE);

  // Running totals over all of the learner's points, before any filter, so
  // an entry shows the same total under every filter.
  const entries = useMemo(() => withRunningTotals(pointsHistory), [pointsHistory]);
  const filtered = useMemo(() => {
    const { matches } = FILTERS.find((filter) => filter.id === filterId);
    return entries.filter(matches);
  }, [entries, filterId]);
  // A day's points count all of its entries under the filter, not only the
  // ones shown so far.
  const dayPoints = useMemo(
    () =>
      filtered.reduce((totals, entry) => {
        const key = dayKey(entry.earnedAt);
        totals[key] = (totals[key] || 0) + (Number(entry.points) || 0);
        return totals;
      }, {}),
    [filtered]
  );

  if (requesting && pointsHistory.length === 0) {
    return (
      <div className="flex justify-content-center p-4">
        <ProgressSpinner />
      </div>
    );
  }

  if (pointsHistory.length === 0) {
    return (
      <EmptyState
        icon="pi-clock"
        message={intl.formatMessage({ id: "no_points_history" })}
      />
    );
  }

  const today = dayjs().format("YYYY-MM-DD");
  const days = groupByDay(filtered.slice(0, shown));

  const chooseFilter = (id) => {
    setFilterId(id);
    setShown(PAGE_SIZE);
  };

  return (
    <div className="lr-points">
      <div
        className="lr-filters"
        role="group"
        aria-label={intl.formatMessage({ id: "my_rewards_filter_label" })}
      >
        {FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={`lr-chip${filter.id === filterId ? " lr-chip--on" : ""}`}
            aria-pressed={filter.id === filterId}
            onClick={() => chooseFilter(filter.id)}
          >
            {intl.formatMessage({ id: filter.label })}
          </button>
        ))}
      </div>

      {days.length === 0 ? (
        <EmptyState
          message={intl.formatMessage({ id: "my_rewards_no_points_for_filter" })}
        />
      ) : (
        <div className="lr-log">
          {days.map((day) => {
            const headingId = `${idPrefix}-${day.key}`;
            const date = formatDate(intl, day.date);
            const points = dayPoints[day.key] || 0;
            return (
              <div
                key={day.key}
                className="lr-day"
                role="group"
                aria-labelledby={headingId}
              >
                <div className="lr-day-header">
                  <h2 id={headingId} className="lr-day-date">
                    {day.key === today
                      ? intl.formatMessage({ id: "my_rewards_today" }, { date })
                      : date}
                  </h2>
                  <span className="lr-day-points">
                    {intl.formatMessage(
                      { id: "my_rewards_day_points" },
                      { points: intl.formatNumber(points), count: points }
                    )}
                  </span>
                </div>
                {/* Safari drops list semantics from a list without bullets. */}
                {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                <ul className="lr-entries" role="list">
                  {day.entries.map((entry) => (
                    <PointsEntry key={entry.id} entry={entry} />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > shown && (
        <button
          type="button"
          className="lr-more"
          onClick={() => setShown((count) => count + PAGE_SIZE)}
        >
          {intl.formatMessage({ id: "my_rewards_show_more" })}
        </button>
      )}
    </div>
  );
};

export default PointsHistory;
